import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useProjects } from "@/hooks/use-projects";
import { createProject, updateProject } from "@/lib/time-api";
import { ProjectFormDialog } from "@/components/time/ProjectFormDialog";
import type { Project } from "@/types/time";

interface ProjectListProps {
  isAdmin: boolean;
}

export function ProjectList({ isAdmin }: ProjectListProps) {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const { projects, loading, refetch } = useProjects();
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Project | null>(null);

  const base = `/${orgId}/${slug}/settings/projects`;

  async function handleCreate(data: { name: string; color?: string }) {
    try {
      const project = await createProject(data);
      toast.success("Project created");
      refetch();
      navigate(`${base}/${project.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create project");
    }
  }

  async function handleArchive(project: Project) {
    try {
      await updateProject(project.id, { isArchived: true });
      toast.success("Project archived");
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive project");
    }
    setArchiveTarget(null);
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading projects...</p>;
  }

  const activeProjects = projects.filter((p) => !p.isArchived);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {isAdmin && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        )}
      </div>

      {activeProjects.length > 0 ? (
        <div className="divide-y">
          {activeProjects.map((project) => (
            <div
              key={project.id}
              className="flex items-center justify-between py-3 cursor-pointer hover:bg-muted/50 -mx-2 px-2 rounded-md transition-colors"
              onClick={() => navigate(`${base}/${project.id}`)}
            >
              <div className="flex items-center gap-3">
                <FolderKanban
                  className="h-5 w-5 shrink-0"
                  style={{ color: project.color ?? "#94A3B8" }}
                />
                <span className="text-sm font-medium">{project.name}</span>
              </div>
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${base}/${project.id}`);
                      }}
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setArchiveTarget(project);
                      }}
                    >
                      Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No projects yet. Create one to get started.
        </p>
      )}

      <ProjectFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />

      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive project?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.name}" will be removed from the active list. Existing time entries are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveTarget && handleArchive(archiveTarget)}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
